import { useCallback, useEffect } from "react";
import { Resolver, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useApiQuery } from "../../../../common/infrastructure/hooks/useApiQuery";
import { useApiMutation } from "../../../../common/infrastructure/hooks/useApiMutation";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { IReviewSettingsModel } from "../../../reviews/models/reviewModels";
import {
  IReviewSettingsFormModel,
  reviewSettingsDefaultValues,
  reviewSettingsSchema,
} from "../models/reviewSettingsFormModel";

const SETTINGS_KEY = [endpointsMetadata.studioReviews.getSettings];

/**
 * "Ревюта → Настройки": e-mail invitations, showing reviews on the home screen, the
 * minimum number of reviews before they show, and the studio's Google review link.
 */
export const useReviewSettings = (onSaved?: () => void) => {
  const form = useForm<IReviewSettingsFormModel>({
    defaultValues: reviewSettingsDefaultValues,
    // The schema is typed against the model; the yup resolver widens its fields to optional.
    resolver: yupResolver(reviewSettingsSchema) as Resolver<IReviewSettingsFormModel>,
    mode: "onChange",
  });

  const { data, isLoading } = useApiQuery<IReviewSettingsModel>({
    queryKey: SETTINGS_KEY,
    endpoint: endpointsMetadata.studioReviews.getSettings,
    globalIsLoading: false,
  });

  useEffect(() => {
    if (data) {
      form.reset({
        reviewRequestsEnabled: data.reviewRequestsEnabled,
        showReviewsPublicly: data.showReviewsPublicly,
        minReviewsToShow: data.minReviewsToShow,
        googleReviewUrl: data.googleReviewUrl ?? "",
      });
    }
  }, [data, form]);

  const { mutate, isLoading: isSaving } = useApiMutation();

  const save = useCallback(
    (values: IReviewSettingsFormModel) => {
      const googleReviewUrl = values.googleReviewUrl?.trim() ?? "";
      mutate(
        {
        endpoint: endpointsMetadata.studioReviews.updateSettings,
        method: "post",
        payload: {
          reviewRequestsEnabled: values.reviewRequestsEnabled,
          showReviewsPublicly: values.showReviewsPublicly,
          minReviewsToShow: Number(values.minReviewsToShow),
          googleReviewUrl: googleReviewUrl || null,
        },
        invalidateKeys: [SETTINGS_KEY],
        successMessage: "Настройките са запазени",
        },
        { onSuccess: () => onSaved?.() },
      );
    },
    [mutate, onSaved],
  );

  return {
    form,
    isLoading,
    isSaving,
    save,
    handleSubmit: form.handleSubmit((values) => save(values)),
  };
};
